import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const GOOGLE_CLIENT_ID = '637959161046-s0noel2snkiv7tksgj1b2acq1ddgm18s.apps.googleusercontent.com';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export function useGoogleSignIn(onSuccess?: () => void) {
  const { toast } = useToast();
  const scriptLoadedRef = useRef(false);

  const handleCredentialResponse = useCallback(async (response: any) => {
    try {
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: response.credential,
      });

      if (error) {
        toast({
          title: 'Google sign in failed',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Welcome!',
        description: 'Signed in with Google successfully.',
      });
      onSuccess?.();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to sign in with Google',
        variant: 'destructive',
      });
    }
  }, [toast, onSuccess]);

  const loadScript = useCallback(() => {
    if (scriptLoadedRef.current || document.getElementById('google-gsi-script')) {
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    scriptLoadedRef.current = true;
  }, []);

  const renderButton = useCallback((elementId: string) => {
    const interval = setInterval(() => {
      const el = document.getElementById(elementId);
      if (window.google && el) {
        clearInterval(interval);
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.renderButton(el, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: '100%',
          logo_alignment: 'left',
        });
      }
    }, 100);

    // Cleanup after 10s
    setTimeout(() => clearInterval(interval), 10000);

    return () => clearInterval(interval);
  }, [handleCredentialResponse]);

  useEffect(() => {
    loadScript();
  }, [loadScript]);

  return { renderButton };
}
